import { useId, useState, type InputHTMLAttributes } from "react";
import { Check, Eye, EyeOff, Lock, X } from "lucide-react";
import { cx } from "../ui";

/**
 * Mirror of src/auth/passwordPolicy.ts. Keep in sync — the server
 * is the source of truth and rejects with 400 + a `failed` array,
 * but the live checklist below is what nudges the user toward a
 * password the server will accept on submit.
 */
export const PASSWORD_MIN_LENGTH = 12;

type RuleKey = "length" | "lowercase" | "uppercase" | "digit" | "symbol";

const RULES: Array<{ key: RuleKey; label: string; test: (p: string) => boolean }> = [
  {
    key: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH && p.length <= 128,
  },
  { key: "lowercase", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "uppercase", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "A number", test: (p) => /[0-9]/.test(p) },
  {
    key: "symbol",
    label: "A symbol (e.g. ! @ # $ %)",
    test: (p) => /[^A-Za-z0-9]/.test(p),
  },
];

export function passwordMeetsPolicy(pw: string): boolean {
  return RULES.every((r) => r.test(pw));
}

type PasswordInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> & {
  value: string;
  onChange: (v: string) => void;
  /**
   * When true, render a leading lock glyph inside the input. The
   * sign-in form does this; the change-password form on Settings
   * leaves it off because the form already has a Lock heading.
   */
  showLockGlyph?: boolean;
};

/**
 * Single-line password input with a click-to-toggle eye that
 * switches the field between type="password" and type="text". Used
 * for every password field in the app so the toggle behavior is
 * consistent.
 */
export function PasswordInput({
  value,
  onChange,
  showLockGlyph = false,
  className,
  ...rest
}: PasswordInputProps) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="relative">
      {showLockGlyph && (
        <Lock
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
        />
      )}
      <input
        {...rest}
        type={reveal ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cx("input pr-10", showLockGlyph && "pl-9", className)}
      />
      <button
        type="button"
        onClick={() => setReveal((r) => !r)}
        aria-label={reveal ? "Hide password" : "Show password"}
        aria-pressed={reveal}
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded p-1 text-ink-400 hover:text-ink-700 hover:bg-sand-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

/**
 * Inline checklist that highlights which password rules are
 * already met for the current value. Renders nothing when value is
 * empty so the form doesn't shout at the user before they type.
 */
export function PasswordPolicyChecklist({ value }: { value: string }) {
  if (!value) return null;
  return (
    <ul className="mt-1.5 space-y-0.5 text-[11.5px] list-none p-0 m-0">
      {RULES.map((r) => {
        const ok = r.test(value);
        return (
          <li
            key={r.key}
            className={cx(
              "inline-flex items-center gap-1.5 mr-3",
              ok ? "text-status-approved-fg" : "text-ink-500",
            )}
          >
            {ok ? (
              <Check size={12} aria-hidden="true" />
            ) : (
              <X size={12} aria-hidden="true" />
            )}
            <span>{r.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Slim strength bar driven by how many rules pass plus a small
 * bonus for length beyond the minimum. Five segments, color-coded.
 */
export function PasswordStrengthMeter({ value }: { value: string }) {
  if (!value) return null;
  const passed = RULES.filter((r) => r.test(value)).length;
  const lengthBonus = value.length >= 16 ? 1 : 0;
  const score = Math.min(5, passed + lengthBonus); // 0..5
  const tone =
    score <= 2
      ? "bg-status-rejected-fg"
      : score <= 3
        ? "bg-status-revisions-fg"
        : score <= 4
          ? "bg-status-review-fg"
          : "bg-status-approved-fg";
  const label =
    score <= 2 ? "Weak" : score <= 3 ? "Fair" : score <= 4 ? "Good" : "Strong";
  const id = useId();
  return (
    <div className="mt-1.5">
      <div
        className="h-1.5 w-full rounded bg-sand-200 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={score}
        aria-labelledby={id}
      >
        <div
          className={cx("h-full transition-all", tone)}
          style={{ width: `${(score / 5) * 100}%` }}
        />
      </div>
      <p id={id} className="mt-1 text-[11px] text-ink-500">
        Strength: <span className="font-medium text-ink-700">{label}</span>
      </p>
    </div>
  );
}
